import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertTriangle, Loader2, X } from "lucide-react";

/**
 * MemedWidget — loads Memed Sinapse Prescrição inside a modal.
 * Flow:
 *  1. Fetch session via POST /api/memed/session/{appointment_id}
 *  2. Inject <script data-token> if not already loaded
 *  3. Wait for MdSinapsePrescricao 'core:moduleInit' event
 *  4. Send setPaciente command
 *  5. Show module (MdHub.module.show)
 *  6. Listen for 'prescricaoImpressa' event → save PDF URL to backend
 */
export default function MemedWidget({ open, onOpenChange, appointment, onSigned }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);
  const scriptRef = useRef(null);

  useEffect(() => {
    if (!open || !appointment) return;
    let cancelled = false;
    setLoading(true); setError(null); setSession(null);
    (async () => {
      try {
        const { data } = await api.post(`/memed/session/${appointment.id}`);
        if (cancelled) return;
        setSession(data);
        await bootstrap(data);
      } catch (err) {
        if (cancelled) return;
        setError(formatApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, appointment]);

  const bootstrap = async (sess) => {
    // Reuse or inject the script tag
    const existing = document.querySelector('script[data-memed-widget="1"]');
    if (existing) {
      // If token differs, remove and re-inject
      if (existing.getAttribute("data-token") !== sess.token) {
        existing.remove();
        window.MdSinapsePrescricao = undefined;
        window.MdHub = undefined;
      } else {
        wireEvents(sess);
        return;
      }
    }
    const s = document.createElement("script");
    s.type = "text/javascript";
    s.src = sess.script_url;
    s.dataset.token = sess.token;
    s.dataset.memedWidget = "1";
    s.async = true;
    s.onload = () => wireEvents(sess);
    s.onerror = () => setError("Falha ao carregar o script da Memed. Cheque se o domínio está liberado no console da Memed.");
    document.body.appendChild(s);
    scriptRef.current = s;
  };

  const wireEvents = (sess) => {
    if (!window.MdSinapsePrescricao || !window.MdHub) {
      setTimeout(() => wireEvents(sess), 300);
      return;
    }
    try {
      window.MdSinapsePrescricao.event.add("core:moduleInit", (module) => {
        if (module?.name !== sess.module_name) return;
        window.MdHub.command.send(sess.module_name, "setPaciente", sess.patient);
        window.MdHub.module.show(sess.module_name);
      });
      // Fires when a prescription is signed & printed by the doctor
      window.MdSinapsePrescricao.event.add("prescricaoImpressa", async (data) => {
        try {
          await api.post("/webhook/memed", {
            event: "prescricaoImpressa",
            external_id: sess.patient.external_id,
            appointment_id: sess.appointment_id,
            ...data,
          });
          toast.success("Prescrição assinada via Memed ✓");
          onSigned?.();
          onOpenChange(false);
        } catch (err) { toast.error(formatApiError(err)); }
      });
    } catch (e) { setError(e.message || "Erro ao inicializar widget Memed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl h-[85vh] p-0 overflow-hidden flex flex-col" data-testid="memed-widget-dialog">
        <DialogHeader className="p-4 border-b border-slate-200 flex-row items-center justify-between space-y-0">
          <DialogTitle className="font-display text-navy-900 flex items-center gap-2">
            <span>Prescrição Digital · Memed</span>
            {session?.environment && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${session.environment === "production" ? "bg-teal-100 text-teal-800" : "bg-amber-100 text-amber-900"}`}>
                {session.environment === "production" ? "PRODUÇÃO" : "SANDBOX"}
              </span>
            )}
          </DialogTitle>
          <button onClick={() => onOpenChange(false)} className="text-slate-400 hover:text-slate-700" data-testid="memed-close">
            <X className="w-4 h-4" />
          </button>
        </DialogHeader>

        <div className="flex-1 relative overflow-auto bg-slate-50">
          {loading && (
            <div className="absolute inset-0 grid place-items-center text-slate-500" data-testid="memed-loading">
              <div className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Carregando prescritor Memed…</div>
            </div>
          )}
          {error && (
            <div className="p-6" data-testid="memed-error">
              <div className="max-w-lg mx-auto rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                <div className="flex gap-2 items-start">
                  <AlertTriangle className="w-4 h-4 mt-0.5" />
                  <div>
                    <b>Não foi possível abrir a Memed.</b>
                    <p className="mt-1">{error}</p>
                    <ul className="mt-2 list-disc pl-4 text-xs">
                      <li>Confirme que seu CPF/CRM está cadastrado na sua instituição Memed</li>
                      <li>Confirme que o paciente preencheu o "Perfil Memed" (CPF, telefone, data de nascimento)</li>
                      <li>Em produção: verifique se as chaves MEMED_API_KEY e MEMED_SECRET_KEY estão configuradas</li>
                    </ul>
                  </div>
                </div>
                <div className="mt-3 text-right">
                  <Button size="sm" variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
                </div>
              </div>
            </div>
          )}
          {/* Anchor onde a Memed injeta o widget — o próprio script cria o iframe */}
          <div id="memed-prescricao" data-testid="memed-anchor" />
        </div>
      </DialogContent>
    </Dialog>
  );
}
